import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, TextField, TextFieldProps } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { LoadingButton } from "@mui/lab";
import classnames from "classnames";
import { DashboardQuerySource } from "../../../types/files";
import { AnswerQuestionResponseType } from "../../../types/search";
import {AuthContext} from "../../../contexts/AuthContext";
import { DashboardContext } from "../../../contexts/DashboardContext";
import { addDashboardQueryFunc } from "../../../lib/helper";

const useStyles = makeStyles((theme) => ({
    dialog: {
        "& .MuiDialog-paper": {
            width: 500,
            height: 250,
            padding: '16px 24px',
            borderRadius: 24,
        }
    },
    dialogTitle: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: 'unset',
        color: 'black',
        fontWeight: 'bold',
        fontFamily: 'Inter',
        fontSize: '1.25rem',
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        padding: '8px 0',
        height: '100%',
        overflowY: 'hidden',
        gap: 8,
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: 0,
        gap: 8,
    },
    inputField: {
        border: `1px solid ${theme.colors.neutral[400]}`,
        borderRadius: 8,
        padding: 8,
        "& .MuiOutlinedInput-root": {
            width: '100%',
            padding: 'unset',
            "& > input, > textarea": {
                fontFamily: 'Lato',
                fontSize: '1.2rem',
                padding: 'unset',
            },
        },
    },
    commonButton: {
        borderRadius: 32,
        textTransform: 'none',
        fontWeight: 'bold',
        minWidth: 80,
    },
    cancelButton: {
        width: 100,
        borderRadius: 32,
        borderColor: theme.colors.neutral['100'],
        backgroundColor: theme.colors.neutral['100'],
        color: theme.colors.neutral['500'],
        textTransform: 'none',
        fontWeight: 'bold',
        transition: 'ease-in-out 300ms',
    },
}));

const AddCustomPeopleModal: React.FC<{
    isOpen: boolean,
    customValues?: { title: string, link?: string},
    onClose: () => void,
}> = ({ isOpen, customValues, onClose }) => {
    const classes = useStyles();
    const { userGroup } = useContext(AuthContext);
    const { dashboard } = useContext(DashboardContext);
    const { dashboardQueries, defaultQueryTemplateValues } = useContext(DashboardContext);

    const [loading, setLoading] = useState<boolean>(false);
    const [saveReady, setSaveReady] = useState<boolean>(false);

    const oldNameValue = useRef<string|null>(null);
    const nameValue = useRef<TextFieldProps>(null);
    const urlValue = useRef<TextFieldProps>(null);

    const handleSave = useCallback(() => {
        setLoading(true);

        const peopleQuery = dashboardQueries.find(query => query.title === 'Key People');
        const peopleAnswers = JSON.parse(peopleQuery?.answer || '{}')?.answer as typeof customValues;
        let newAnswer: any[] = [];

        if (Array.isArray(peopleAnswers)) {
            if (!!peopleAnswers.find(people => people.title === oldNameValue.current)) {
                newAnswer = [...peopleAnswers].map(people => people.title === oldNameValue.current ? ({
                    link: urlValue.current?.value,
                    title: nameValue.current?.value,
                    content: 'LinkedIn profile goes here ...'
                }) : people);
            } else {
                newAnswer = [...peopleAnswers, {
                    link: urlValue.current?.value,
                    title: nameValue.current?.value,
                    content: 'LinkedIn profile goes here ...'
                }];
            }
        } else {
            newAnswer = [{
                link: urlValue.current?.value,
                title: nameValue.current?.value,
                content: 'LinkedIn profile goes here ...'
            }];
        }

        addDashboardQueryFunc(
            dashboard!.id,
            'Key People',
            userGroup!,
            dashboard!.title,
            JSON.stringify({
                answer: newAnswer,
                type: AnswerQuestionResponseType.URL_LIST,
            }),
            undefined,
            defaultQueryTemplateValues.find(defQuery => defQuery.title === 'Key People')?.order || 1,
            true,
            DashboardQuerySource.MANUAL,
        ).then(() => {
            setLoading(false);
            onClose();
        });
    // eslint-disable-next-line
    }, [dashboard, defaultQueryTemplateValues, dashboardQueries, userGroup]);

    useEffect(() => {
        if (!!customValues) {
            if (!!urlValue.current)
                urlValue.current.value = customValues.link;
            if (!!nameValue.current)
                nameValue.current.value = customValues.title;
            if (!!oldNameValue)
                oldNameValue.current = customValues.title;
        }
    },[customValues]);

    return (<>
        <Dialog className={classes.dialog} open={isOpen} onClose={() => onClose()}>
            <DialogTitle className={classes.dialogTitle}>
                {`Add/edit person`}
            </DialogTitle>
            <Divider sx={{ margin: '8px 0'}} />
            <DialogContent className={classes.dialogContent}>
                <TextField variant="outlined"
                    className={classnames('no-border', classes.inputField)}
                    inputProps={{ style: { height: '100%' }, className: 'no-fade' }}
                    placeholder={'Name ...'}
                    defaultValue={customValues?.title}
                    onChange={() => { setSaveReady(!!nameValue.current?.value); }}
                    inputRef={nameValue}
                    disabled={loading}
                    rows={1}
                />
                <TextField variant="outlined"
                    className={classnames('no-border', classes.inputField)}
                    inputProps={{ style: { height: '100%' }, className: 'no-fade' }}
                    placeholder={'LinkedIn Profile URL ...'}
                    defaultValue={customValues?.link}
                    onChange={() => setSaveReady(!!urlValue.current?.value)}
                    inputRef={urlValue}
                    disabled={loading}
                    rows={1}
                />
            </DialogContent>
            <Divider sx={{ margin: '8px 0'}} />
            <DialogActions className={classes.dialogActions}>
                <Button
                    variant="outlined"
                    className={classes.cancelButton}
                    onClick={() => onClose()}
                > Cancel </Button>
                <LoadingButton
                    variant="contained"
                    className={classes.commonButton}
                    loading={loading}
                    disabled={!saveReady}
                    onClick={handleSave}
                > Save </LoadingButton>
            </DialogActions>
        </Dialog>
    </>);
}

export default AddCustomPeopleModal;